"use client";

/**
 * Plaid Link「第三方跳转段」埋点门面。
 *
 * ## 为什么单独一层
 *
 * 在此之前，从「我们调 open()」到「Plaid 回调 success/exit」这一整段在 OpenReplay 里是黑盒：
 * `usePlaidLinkFlow` 只有 `Track.error` / `Track.log`（错误通道，不进事件流），
 * 于是「点了 Connect → Plaid 打开 → 用户在机构选择页放弃」只能靠
 * `*_connect_chain_end{stage:"plaid", result:"close"}` 反推，拆不出**在哪一步**掉的。
 *
 * Plaid Web SDK（`react-plaid-link` 的 `onEvent`）本来就把整条流推给我们，之前只是没接。
 *
 * | 事件 | 时机 | 回答的问题 |
 * |------|------|-----------|
 * | `plaid_link_open` | **发起**打开（拿到 token、即将把 SDK 拉起） | 有多少次真的走到了第三方 |
 * | `plaid_link_step` | SDK 每一个 onEvent（**仅 web**） | 用户走到哪一步、在哪一步停住 |
 * | `plaid_link_exit`  | Link 关闭（用户取消 / 报错 / 没能拉起） | 掉在哪、为什么掉 |
 * | `plaid_link_success` | 授权交换成功、拿到 connection | 分子 |
 *
 * ## 四条事件必须同源
 *
 * `plaid_link_success` 原来只由 `features/financial-analysis-entry` 一个入口发
 * （`reportFinancialPlaidLinkSuccess`），而 open/exit 在 `usePlaidLinkFlow` 里、**所有**入口都发。
 * 覆盖面不对等，`success / open` 这个比值就是错的（被其它入口的 open 稀释）。
 * 所以四条统一收在这一层、由同一个 hook 发射 —— 分子分母同源是这套埋点唯一有意义的前提。
 *
 * ## open 是「发起」不是「已打开」
 *
 * web 在 `mountWebSession` 时发，此时 SDK 还没 `ready`；native 在 `launchPlaidLink` 前发，
 * 而并发防护可能直接拒绝（`request_in_progress`）。两端都是**发起**语义，一致。
 * 要「SDK 真的起来了」用 web 的 `plaid_link_step{step:"open"}`；
 * 要扣掉根本没拉起来的，减去 `plaid_link_exit{not_launched:true}`。
 *
 * ## mode 维度
 *
 * 三种承载的可观测深度不同，混在一起算转化率会得到错误结论，所以每条都带 `mode`：
 * - `web`：浏览器内 Plaid iframe（SDK 事件流**完整**）
 * - `in_app`：App WebView 里经 bridge `plaid.openLink` 交给原生壳（**拿不到** SDK 内部事件流）
 * - `native`：KMP 原生自己起 SDK（见 `kmp-app/.../data/plaid/PlaidLinkFlow.kt`，同样拿不到）
 *
 * 规范：`docs/engineering/tracking/tracking-conventions.md` §1.6（参数最小集：机构 id 是这里的唯一 id）。
 */

import { Track } from "@/src/lib/tracking";

/** 承载方式 —— 三者的可观测深度不同，聚合前必须先按它拆开。 */
export type PlaidLinkMode = "web" | "in_app";

/** 打开 Plaid Link 的入口形态。`oauth_resume` = 从银行 OAuth 页回跳后续跑同一个 link_token。 */
export type PlaidLinkOpenTrigger = "user" | "oauth_resume" | "rebind";

/**
 * 调用方补充的业务维度，四条事件原样透传。
 *
 * 用途：让「哪个业务入口发起的连接」进得了 Plaid 段的数据 —— 例如 financial-analysis-entry
 * 传 `surface/board/entry/account_target`，这样 Plaid 段的成功率可以按 board 拆。
 * hook 自己不认识这些字段，也不该认识（它只管 Plaid，不管谁调它）。
 *
 * 值必须是扁平标量（规范 §3.2）。
 */
export type PlaidLinkTrackingContext = Record<
  string,
  string | number | boolean | null | undefined
>;

export interface PlaidLinkOpenParams {
  mode: PlaidLinkMode;
  trigger: PlaidLinkOpenTrigger;
  /** update mode（重连既有连接）而非首次绑定 */
  isUpdateMode: boolean;
  context?: PlaidLinkTrackingContext;
}

/**
 * Plaid `onEvent` 的 metadata 里我们要的字段。
 *
 * 全量字段见 Plaid 文档 `onEvent` metadata；这里只取能回答「哪一步 / 哪家机构 / 什么错」的，
 * 不整包透传 —— metadata 还有一批我们用不到的时间戳字段，全带上只会让 payload 变宽
 * 而不增加可回答的问题。
 */
export interface PlaidLinkEventMetadata {
  view_name?: string | null;
  institution_id?: string | null;
  institution_name?: string | null;
  error_code?: string | null;
  error_type?: string | null;
  error_message?: string | null;
  exit_status?: string | null;
  mfa_type?: string | null;
  request_id?: string | null;
  link_session_id?: string | null;
}

export interface PlaidLinkExitParams {
  mode: PlaidLinkMode;
  /** 用户主动关掉（非报错）—— 这两类的处置完全不同，必须在数据里分开 */
  isUserCancel: boolean;
  /**
   * SDK **根本没被拉起**就结束了（并发防护拒绝等）—— 这类要从 `plaid_link_open` 里减掉，
   * 否则分母虚高。只在为 true 时出键。
   */
  notLaunched?: boolean;
  metadata?: PlaidLinkEventMetadata;
  context?: PlaidLinkTrackingContext;
}

export interface PlaidLinkSuccessParams {
  mode: PlaidLinkMode;
  isUpdateMode: boolean;
  context?: PlaidLinkTrackingContext;
}

/** 只保留有值的标量：undefined/null/"" 一律不出键（对齐 §3.2，空串在后台等于噪声）。 */
function compact(
  params: Record<string, string | number | boolean | null | undefined>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") {
      continue;
    }
    out[key] = value;
  }
  return out;
}

function metadataParams(
  metadata: PlaidLinkEventMetadata | undefined,
): Record<string, string | number | boolean | null | undefined> {
  if (!metadata) {
    return {};
  }
  return {
    view_name: metadata.view_name,
    institution_id: metadata.institution_id,
    institution_name: metadata.institution_name,
    error_code: metadata.error_code,
    error_type: metadata.error_type,
    error_message: metadata.error_message,
    exit_status: metadata.exit_status,
    mfa_type: metadata.mfa_type,
    request_id: metadata.request_id,
    link_session_id: metadata.link_session_id,
  };
}

/**
 * 我们**发起**了 Plaid Link（web：即将挂载 SDK 会话；in_app：bridge 已发出）。
 *
 * 不等于「SDK 已经显示出来」—— 见文件头「open 是发起不是已打开」。
 */
export function trackPlaidLinkOpen(params: PlaidLinkOpenParams): void {
  Track.event(
    "plaid_link_open",
    compact({
      ...params.context,
      mode: params.mode,
      trigger: params.trigger,
      is_update_mode: params.isUpdateMode,
    }),
  );
}

/**
 * Plaid SDK 的一步（`onEvent`）。`step` 取 SDK 的 eventName 转小写
 * （`OPEN` / `SELECT_INSTITUTION` / `OPEN_OAUTH` / `SUBMIT_CREDENTIALS` /
 * `TRANSITION_VIEW` / `HANDOFF` / `ERROR` / `EXIT` …）。
 *
 * **只有 web 模式有这条** —— in_app / native 的 SDK 跑在壳里，回调不过 JS。
 * ⚠️ 这是 Plaid 段里量最大的事件：一次会话十几到几十条，别和点击类放一个量级比。
 */
export function trackPlaidLinkStep(
  step: string,
  metadata?: PlaidLinkEventMetadata,
  context?: PlaidLinkTrackingContext,
): void {
  Track.event(
    "plaid_link_step",
    compact({
      ...context,
      mode: "web",
      step: step.toLowerCase(),
      ...metadataParams(metadata),
    }),
  );
}

/** Plaid Link 关闭 —— 用户取消、报错、或压根没拉起来。成功不走这条。 */
export function trackPlaidLinkExit(params: PlaidLinkExitParams): void {
  Track.event(
    "plaid_link_exit",
    compact({
      ...params.context,
      mode: params.mode,
      is_user_cancel: params.isUserCancel,
      not_launched: params.notLaunched ? true : undefined,
      ...metadataParams(params.metadata),
    }),
  );
}

/**
 * Plaid 绑定成功（授权交换完成、拿到 connection）。
 *
 * 与 open/exit **同一层发射**，覆盖面才对等 —— 见文件头「四条事件必须同源」。
 */
export function trackPlaidLinkSuccess(params: PlaidLinkSuccessParams): void {
  Track.event(
    "plaid_link_success",
    compact({
      ...params.context,
      mode: params.mode,
      is_update_mode: params.isUpdateMode,
    }),
  );
}
